import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { supabase } from './supabase';
import type {
    ActivityLogEntry,
    Builder,
    Community,
    Crew,
    JobWithContext,
    ServiceRequestStatus,
    ServiceRequestType,
    ServiceRequestWithContext,
    Stage,
    WeatherHold,
} from './database.types';

// -----------------------------------------------------------------------------
// Query keys
// -----------------------------------------------------------------------------

export const queryKeys = {
    currentUser: ['currentUser'] as const,
    stages: ['stages'] as const,
    crews: ['crews'] as const,
    builders: ['builders'] as const,
    communities: ['communities'] as const,
    jobs: {
        all: ['jobs'] as const,
        list: (filters?: { needsReview?: boolean; builderId?: string }) =>
            ['jobs', 'list', filters ?? {}] as const,
        detail: (id: string) => ['jobs', 'detail', id] as const,
        todaysStages: (userId?: string) => ['jobs', 'today', userId] as const,
    },
    weatherHolds: {
        active: (jobId: string) => ['weatherHolds', 'active', jobId] as const,
    },
    activityLog: {
        recent: (limit: number) => ['activityLog', 'recent', limit] as const,
        forEntity: (entityType: string, entityId: string) =>
            ['activityLog', entityType, entityId] as const,
    },
} as const;

// -----------------------------------------------------------------------------
// Invalidation helper
// -----------------------------------------------------------------------------

export function useInvalidateJobs() {
    const qc = useQueryClient();
    return (jobId?: string) => {
        qc.invalidateQueries({ queryKey: queryKeys.jobs.all });
        if (jobId) {
            qc.invalidateQueries({ queryKey: queryKeys.jobs.detail(jobId) });
        }
        qc.invalidateQueries({ queryKey: ['activityLog'] });
    };
}

// -----------------------------------------------------------------------------
// Reference data
// -----------------------------------------------------------------------------

export function useStages() {
    return useQuery({
        queryKey: queryKeys.stages,
        queryFn: async (): Promise<Stage[]> => {
            const { data, error } = await supabase
                .from('stages')
                .select('*')
                .order('sequence_order');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 60, // reference data rarely changes
    });
}

export function useCrews() {
    return useQuery({
        queryKey: queryKeys.crews,
        queryFn: async (): Promise<Crew[]> => {
            const { data, error } = await supabase
                .from('crews')
                .select('*')
                .eq('active', true)
                .order('name');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 10,
    });
}

export function useBuilders() {
    return useQuery({
        queryKey: queryKeys.builders,
        queryFn: async (): Promise<Builder[]> => {
            const { data, error } = await supabase
                .from('builders')
                .select('*')
                .eq('active', true)
                .order('name');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 30,
    });
}

export function useCommunities() {
    return useQuery({
        queryKey: queryKeys.communities,
        queryFn: async (): Promise<Community[]> => {
            const { data, error } = await supabase
                .from('communities')
                .select('*')
                .eq('active', true)
                .order('name');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 10,
    });
}

// -----------------------------------------------------------------------------
// Jobs
// -----------------------------------------------------------------------------

// Two crew embeds, at two different levels, and they are not the same thing.
// The top level one follows jobs.crew_id and answers "whose job is this", which
// is what the board's Crew column, its filter and the CSV export read. The one
// inside stages follows job_stages.crew_id and answers "who is on Box", which
// the field still uses. jobs has exactly one foreign key to crews, so the
// unqualified embed resolves without a disambiguating hint.
const JOB_SELECT = `
    *,
    builder:builders ( id, name, prefix ),
    community:communities ( id, name ),
    crew:crews ( id, name ),
    stages:job_stages (
        *,
        stage:stages ( * ),
        crew:crews ( id, name )
    )
`;

export function useJobs(filters?: { needsReview?: boolean; builderId?: string }) {
    return useQuery({
        queryKey: queryKeys.jobs.list(filters),
        queryFn: async (): Promise<JobWithContext[]> => {
            let q = supabase
                .from('jobs')
                .select(JOB_SELECT)
                .eq('active', true)
                .order('updated_at', { ascending: false });

            if (filters?.needsReview) q = q.eq('needs_review', true);
            if (filters?.builderId) q = q.eq('builder_id', filters.builderId);

            const { data, error } = await q;
            if (error) throw error;
            return (data ?? []) as unknown as JobWithContext[];
        },
        // Polling for fresh data every 30s. Realtime subscriptions could
        // replace this in a future iteration; polling is fine for the
        // 60-jobs-at-a-time workload and avoids subscription complexity.
        refetchInterval: 30_000,
        staleTime: 15_000,
    });
}

export function useJob(id: string | undefined) {
    return useQuery({
        queryKey: id ? queryKeys.jobs.detail(id) : ['jobs', 'detail', 'none'],
        enabled: Boolean(id),
        queryFn: async (): Promise<JobWithContext | null> => {
            if (!id) return null;
            const { data, error } = await supabase
                .from('jobs')
                .select(JOB_SELECT)
                .eq('id', id)
                .single();
            if (error) throw error;
            return data as unknown as JobWithContext;
        },
    });
}

// -----------------------------------------------------------------------------
// Weather holds
// -----------------------------------------------------------------------------

export function useActiveWeatherHolds(jobId: string | undefined) {
    return useQuery({
        queryKey: jobId ? queryKeys.weatherHolds.active(jobId) : ['weatherHolds', 'active', 'none'],
        enabled: Boolean(jobId),
        queryFn: async (): Promise<WeatherHold[]> => {
            if (!jobId) return [];
            const { data, error } = await supabase
                .from('weather_holds')
                .select('*')
                .eq('job_id', jobId)
                .is('hold_end', null)
                .order('hold_start', { ascending: false });
            if (error) throw error;
            return data ?? [];
        },
    });
}

// -----------------------------------------------------------------------------
// Activity log
// -----------------------------------------------------------------------------

export function useRecentActivity(
    limit = 100,
    options?: Omit<UseQueryOptions<ActivityLogEntry[], Error>, 'queryKey' | 'queryFn'>
) {
    return useQuery<ActivityLogEntry[], Error>({
        queryKey: queryKeys.activityLog.recent(limit),
        queryFn: async () => {
            const { data, error } = await supabase
                .from('activity_log')
                .select('*')
                .order('created_at', { ascending: false })
                .limit(limit);
            if (error) throw error;
            return (data ?? []) as ActivityLogEntry[];
        },
        refetchInterval: 60_000,
        ...options,
    });
}

export function useEntityActivity(entityType: string, entityId: string | undefined) {
    return useQuery({
        queryKey: entityId
            ? queryKeys.activityLog.forEntity(entityType, entityId)
            : ['activityLog', entityType, 'none'],
        enabled: Boolean(entityId),
        queryFn: async (): Promise<ActivityLogEntry[]> => {
            if (!entityId) return [];
            const { data, error } = await supabase
                .from('activity_log')
                .select('*')
                .eq('entity_type', entityType)
                .eq('entity_id', entityId)
                .order('created_at', { ascending: false })
                .limit(50);
            if (error) throw error;
            return (data ?? []) as ActivityLogEntry[];
        },
    });
}

// -----------------------------------------------------------------------------
// Service requests
// -----------------------------------------------------------------------------

const SERVICE_REQUEST_SELECT = `
    *,
    request_type:service_request_types ( id, code, label ),
    assigned_crew:crews ( id, name ),
    job:jobs ( id, lot_number, community:communities ( name ) )
`;

export function useServiceRequestTypes() {
    return useQuery({
        queryKey: ['serviceRequestTypes'],
        queryFn: async (): Promise<ServiceRequestType[]> => {
            const { data, error } = await supabase
                .from('service_request_types')
                .select('*')
                .eq('active', true)
                .order('display_order');
            if (error) throw error;
            return data ?? [];
        },
        staleTime: 1000 * 60 * 30, // reference data rarely changes
    });
}

export function useServiceRequests(filters?: { status?: ServiceRequestStatus[] }) {
    return useQuery({
        queryKey: ['serviceRequests', 'list', filters ?? {}],
        queryFn: async (): Promise<ServiceRequestWithContext[]> => {
            let q = supabase
                .from('service_requests')
                .select(SERVICE_REQUEST_SELECT);

            if (filters?.status && filters.status.length > 0) {
                q = q.in('status', filters.status);
            }

            // Triage order: highest priority first, oldest first within a priority.
            const { data, error } = await q
                .order('priority', { ascending: false })
                .order('created_at', { ascending: true });
            if (error) throw error;
            return (data ?? []) as unknown as ServiceRequestWithContext[];
        },
        refetchInterval: 30_000,
        staleTime: 15_000,
    });
}
